/**
 * Airport data types for autocomplete and location form integration
 */

/**
 * Raw airport data from the AV8 airports API
 */
export interface AirportSearchResult {
  /** FAA identifier (e.g., "PHX") */
  ARPT_ID: string
  /** ICAO code (e.g., "KPHX") */
  ICAO_ID: string
  /** Airport name (e.g., "Phoenix Sky Harbor Intl") */
  ARPT_NAME: string
  /** City name */
  CITY: string
  /** 2-letter state code (e.g., "AZ") */
  STATE_CODE: string
  /** Full state name (e.g., "Arizona") */
  STATE_NAME?: string
  /** Latitude in decimal degrees (API returns as string) */
  LAT_DECIMAL?: string | number
  /** Longitude in decimal degrees (API returns as string) */
  LONG_DECIMAL?: string | number
  /** Elevation in feet */
  ELEV?: number
  /** Available fuel types */
  FUEL_TYPES?: string
  /** Ownership type code (PU=Public, PR=Private) */
  OWNERSHIP_TYPE_CODE?: string
  /** Facility use code */
  FACILITY_USE_CODE?: string
  /** Tower type code (ATCT=Air Traffic Control Tower) */
  TWR_TYPE_CODE?: string
}

/**
 * Formatted airport item for autocomplete display
 */
export interface AirportAutocompleteItem {
  /** ICAO code for display/storage */
  code: string
  /** FAA/IATA code */
  faaCode: string
  /** Airport name */
  name: string
  /** City name */
  city: string
  /** State code (2-letter) */
  state: string
  /** Latitude */
  latitude?: number
  /** Longitude */
  longitude?: number
  /** Formatted label: "KPHX - Phoenix Sky Harbor Intl (Phoenix, AZ)" */
  label: string
}

/**
 * API response wrapper from airport-search edge function
 */
export interface AirportSearchResponse {
  status: boolean
  data?: AirportSearchResult[]
  error?: string
}

/**
 * Transform raw API result to autocomplete item
 */
export function toAutocompleteItem(airport: AirportSearchResult): AirportAutocompleteItem {
  // API returns LAT_DECIMAL/LONG_DECIMAL as strings; parse to numbers
  const lat = airport.LAT_DECIMAL != null ? Number(airport.LAT_DECIMAL) : undefined
  const lng = airport.LONG_DECIMAL != null ? Number(airport.LONG_DECIMAL) : undefined

  return {
    code: airport.ICAO_ID || airport.ARPT_ID,
    faaCode: airport.ARPT_ID,
    name: airport.ARPT_NAME,
    city: airport.CITY,
    state: airport.STATE_CODE,
    latitude: lat != null && !isNaN(lat) ? lat : undefined,
    longitude: lng != null && !isNaN(lng) ? lng : undefined,
    label: `${airport.ICAO_ID || airport.ARPT_ID} - ${airport.ARPT_NAME} (${airport.CITY}, ${airport.STATE_CODE})`,
  }
}
